import { CSSProperties, ReactElement, useMemo } from 'react';

import Button from '@/common/Button';
import ReportTableCell from '@/pages/Reports/DynamicReport/components/ReportTable/ReportTableCell';
import ReportTableSubHeader from '@/pages/Reports/DynamicReport/components/ReportTable/ReportTableSubHeader';
import { Row } from '@/models/gen/graphql';
import Table from '@/common/Table';
import { TableCollapseProps } from '@/common/Table/TableRow';
import { getClasses } from '@/utils/strings';
import { progressColorFromValue } from '@/pages/Reports/DynamicReport/components/ReportTable/components/StatusCell';
import usePrinting from '@/hooks/usePrinting';
import { useReportTable } from '@/pages/Reports/DynamicReport/components/ReportTable';

export type ReportTableRowProps = Omit<TableCollapseProps, 'content'> & {
  columns: Record<string, unknown>;
  index: number;
  row: Row;
  content?: ReactElement;
  onDetails?: (detailsObj: Record<string, unknown>) => void;
};

const ReportTableRow = ({ columns, row, onDetails }: ReportTableRowProps): ReactElement => {
  const printing = usePrinting();
  const ownColumns = ((row as Record<string, unknown>)?.columns as Record<string, any>) || columns;
  const childColumns = ((row as Record<string, unknown>)?.childColumns as Record<string, any>) || ownColumns;
  const [{ expanded: EXPANDED, onExpand }] = useReportTable(({ state, setState }) => {
    const rowId = row?.['id'];
    const childIds = state.getIds(row);
    const expanded = state.expanded.includes(rowId);
    return [
      {
        expanded,
        onExpand: () => {
          if (!expanded) {
            setState((current) => ({ ...current, expanded: Array.from(new Set([...current.expanded, rowId])) }));
          } else {
            setState((current) => ({
              ...current,
              expanded: current.expanded.filter((id: string) => !childIds.includes(id)),
            }));
          }
        },
      },
    ];
  });

  const content = useMemo(
    () => (
      <>
        <Table.Cell className="ReportTableCell SELECT">
          {row?.children?.length > 0 ? (
            <Button onClick={onExpand} variant="text">
              <i className={`sv ${EXPANDED ? 'sv-minus-square' : 'sv-plus-square'}`} />
            </Button>
          ) : null}
        </Table.Cell>
        {Object.keys(ownColumns || {}).map(
          (col: string): ReactElement => (
            <ReportTableCell className={col.toUpperCase()} key={col}>
              {row?.values?.[col] ?? null}
            </ReportTableCell>
          )
        )}
      </>
    ),
    [row?.children?.length, row?.values, onExpand, EXPANDED, ownColumns]
  );
  const nested = useMemo(
    () =>
      row?.children?.map(
        (child: Row, childIndex: number): ReactElement => (
          <ReportTableRow columns={childColumns} row={child} index={childIndex} key={childIndex} onDetails={onDetails} />
        )
      ),
    [childColumns, onDetails, row]
  );

  const hasNestedColumns = childColumns !== ownColumns;

  const rowStyle = useMemo((): CSSProperties | undefined => {
    const progressColor = Object.values(row?.values || {})
      .map(progressColorFromValue)
      .find((color) => color !== undefined);
    return progressColor !== undefined ? ({ '--progress-color': progressColor } as CSSProperties) : undefined;
  }, [row?.values]);

  if (row.children?.length > 0) {
    return (
      <Table.Collapse
        id={row?.['id']}
        className={getClasses('ReportTableRow', { EXPANDED })}
        style={rowStyle}
        content={
          <Table
            className={hasNestedColumns ? 'ReportNestedTable' : undefined}
            header={hasNestedColumns ? <ReportTableSubHeader columns={childColumns} /> : undefined}
          >
            {nested}
          </Table>
        }
        in={EXPANDED}
        virtualize={!printing}
        onDoubleClick={() => onDetails?.(row?.['filters'] || {})}
      >
        {content}
      </Table.Collapse>
    );
  }
  return (
    <Table.Row
      id={row?.['id']}
      className={getClasses('ReportTableRow')}
      style={rowStyle}
      virtualize={!printing}
      onDoubleClick={() => onDetails?.(row?.['filters'] || {})}
    >
      {content}
    </Table.Row>
  );
};

export default ReportTableRow;
